import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, Upload, Trash2, Download, Plus, ChevronLeft, ChevronRight, User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DailyVouchers() {
  const { token } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({ driver_id: '', notes: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [selectedDate]);

  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setDrivers(res.data.filter(u => u.role === 'driver' || u.role === 'admin-driver'));
    } catch { /* ignore */ }
  };

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daily-vouchers?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVouchers(res.data);
    } catch {
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.driver_id || !uploadFile) {
      toast.error('Select a driver and a file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('driver_id', uploadData.driver_id);
      formData.append('date', selectedDate);
      formData.append('notes', uploadData.notes || '');
      formData.append('file', uploadFile);
      await axios.post(`${API}/daily-vouchers`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Voucher uploaded');
      setShowUpload(false);
      setUploadData({ driver_id: '', notes: '' });
      setUploadFile(null);
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this voucher?')) return;
    try {
      await axios.delete(`${API}/daily-vouchers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voucher deleted');
      fetchVouchers();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDownload = async (voucher) => {
    try {
      const response = await fetch(`${API}/daily-vouchers/${voucher.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = voucher.filename || 'voucher';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  const grouped = drivers.reduce((acc, driver) => {
    const driverVouchers = vouchers.filter(v => v.driver_id === driver.id);
    if (driverVouchers.length > 0) {
      acc.push({ driver, vouchers: driverVouchers });
    }
    return acc;
  }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Daily Vouchers</h1>
          <p className="text-sm text-slate-500">Assign vouchers to drivers for a specific day</p>
        </div>
        <Button
          onClick={() => setShowUpload(true)}
          className="bg-slate-800 hover:bg-slate-700"
          data-testid="add-voucher-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Voucher
        </Button>
      </div>

      {/* Date Picker */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="prev-day-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-44 text-center font-medium"
          data-testid="date-picker"
        />
        <Button variant="outline" size="icon" onClick={() => changeDate(1)} data-testid="next-day-btn">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Upload Dialog */}
      {showUpload && (
        <Card className="mb-6 border-2 border-slate-800">
          <CardContent className="p-5 space-y-4">
            <h3 className="font-semibold text-slate-800">Upload Voucher for {selectedDate}</h3>
            <div>
              <Label>Driver</Label>
              <select
                value={uploadData.driver_id}
                onChange={(e) => setUploadData({ ...uploadData, driver_id: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="voucher-driver-select"
              >
                <option value="">Select driver...</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>File</Label>
              <div 
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-md p-4 text-center cursor-pointer hover:border-slate-400 transition-colors"
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  data-testid="voucher-file-input"
                />
                {uploadFile ? (
                  <p className="text-sm font-medium text-slate-700">{uploadFile.name}</p>
                ) : (
                  <div className="text-slate-400">
                    <Upload className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm">Click to select file</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                placeholder="Optional notes"
                rows={2}
                data-testid="voucher-notes"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowUpload(false); setUploadFile(null); }}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-slate-800 hover:bg-slate-700" data-testid="voucher-upload-btn">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Voucher List */}
      {loading ? (
        <p className="text-center text-slate-500 py-8">Loading...</p>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No vouchers for this day</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ driver, vouchers: dv }) => (
            <Card key={driver.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {driver.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <span className="font-semibold text-slate-800">{driver.name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{dv.length} voucher{dv.length > 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {dv.map(v => (
                    <div key={v.id} className="flex items-center gap-3 bg-slate-50 rounded-md px-3 py-2">
                      <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{v.filename}</p>
                        {v.notes && <p className="text-xs text-slate-400 truncate">{v.notes}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-500 hover:text-blue-600"
                          onClick={() => handleDownload(v)}
                          data-testid={`download-voucher-${v.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-500 hover:text-red-600"
                          onClick={() => handleDelete(v.id)}
                          data-testid={`delete-voucher-${v.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
